import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

function FAQ() {
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  const leftInView = useInView(leftRef, { once: true });
  const rightInView = useInView(rightRef, { once: true });

  return (
    <div className="w-full py-16 px-6 md:px-12 lg:px-16 bg-gray-50">
      {/* Outer Container */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
        {/* Left Section */}
        <motion.div
          ref={leftRef}
          initial={{ opacity: 0, x: -50 }}
          animate={leftInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="lg:w-1/2"
        >
          <h2 className="text-4xl font-semibold text-customAqua600 mb-4">
            Foire Aux Questions
          </h2>
          <p className="text-lg text-customAqua600">
            Vous avez des questions ? Nous avons les réponses ! Consultez les
            questions fréquemment posées pour en savoir plus sur nos services,
            nos horaires, nos tarifs, et bien plus encore.
          </p>
        </motion.div>

        {/* Right Section */}
        <motion.div
          ref={rightRef}
          initial={{ opacity: 0, x: 50 }}
          animate={rightInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="lg:w-1/2 bg-white border border-gray-200 divide-y divide-gray-200 rounded-xl m-4 md:m-8 lg:m-12"
        >
          <details className="p-6 group" open>
            <summary className="flex items-center justify-between cursor-pointer">
              <h5 className="text-sm md:text-md lg:text-lg font-medium text-gray-900">
                Quels sont vos horaires d'ouverture ?
              </h5>
              <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute inset-0 w-5 h-5 opacity-100 group-open:opacity-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute inset-0 w-5 h-5 opacity-0 group-open:opacity-100"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            </summary>
            <p className="mt-4 leading-relaxed text-gray-700">
              Nous sommes ouverts du lundi au samedi, de 9h à 18h. Nous sommes
              fermés le dimanche pour permettre à notre équipe de se reposer.
            </p>
          </details>

          <details className="p-6 group">
            <summary className="flex items-center justify-between cursor-pointer">
              <h5 className="text-sm md:text-md lg:text-lg font-medium text-gray-900">
                Offrez-vous des consultations d'urgence ?
              </h5>
              <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute inset-0 w-5 h-5 opacity-100 group-open:opacity-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute inset-0 w-5 h-5 opacity-0 group-open:opacity-100"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            </summary>
            <p className="mt-4 leading-relaxed text-gray-700">
              Oui, nous proposons des consultations d'urgence. Veuillez nous
              appeler au préalable pour confirmer la disponibilité et réduire
              votre temps d'attente.
            </p>
          </details>

          <details className="p-6 group">
            <summary className="flex items-center justify-between cursor-pointer">
              <h5 className="text-sm md:text-md lg:text-lg font-medium text-gray-900">
                Quels services proposez-vous ?
              </h5>
              <span className="relative flex-shrink-0 ml-1.5 w-5 h-5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute inset-0 w-5 h-5 opacity-100 group-open:opacity-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="absolute inset-0 w-5 h-5 opacity-0 group-open:opacity-100"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            </summary>
            <p className="mt-4 leading-relaxed text-gray-700">
              Nous offrons une gamme complète de services dentaires, y compris
              des nettoyages professionnels, des soins de blanchiment, des
              traitements des caries, des implants et bien plus encore.
            </p>
          </details>
        </motion.div>
      </div>
    </div>
  );
}

export default FAQ;
